import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, endpoints } from "@/lib/api";
import { fmtMoney, fmtDate, todayISO } from "@/lib/utils-format";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, Clock, Briefcase, DollarSign, Award, Wallet, Plus, Trash2 } from "lucide-react";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/employees/${id}/stats`);
      setData(r.data);
    } catch {
      toast.error("Employee not found");
      navigate("/employees");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const togglePaid = async (it) => {
    const next = it.payment_status === "Paid" ? "Unpaid" : "Paid";
    await endpoints.update("job_pay_items", it.id, { ...it, payment_status: next });
    load();
  };

  const removeItem = async (it) => {
    if (!window.confirm("Delete this pay item?")) return;
    await endpoints.remove("job_pay_items", it.id);
    toast.success("Pay item deleted");
    load();
  };

  if (!data) return <Layout title="Employee"><div className="text-muted-foreground">Loading...</div></Layout>;

  const { employee, current_period: cur, ytd, all_time, jobs, pay_items } = data;
  const items = pay_items || [];
  const totals = computeTotals(items);

  return (
    <Layout
      title={`${employee.first_name} ${employee.last_name}`}
      subtitle={`${employee.role}${employee.crew ? " · " + employee.crew : ""} · Payroll Dashboard`}
    >
      <Link to="/employees" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="employee-back">
        <ArrowLeft className="size-4 mr-1"/>Back to employees
      </Link>

      {/* Profile card */}
      <Card className="p-6 rounded-xl mb-6" data-testid="employee-profile">
        <div className="flex flex-wrap items-center gap-6">
          <div className="size-20 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-2xl">
            {employee.first_name?.[0]}{employee.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-display text-2xl font-bold">{employee.first_name} {employee.last_name}</div>
            <div className="text-sm text-muted-foreground">{employee.role}{employee.crew ? " · " + employee.crew : ""}</div>
            <div className="flex flex-wrap gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1.5"><Phone className="size-3"/>{employee.phone || "—"}</span>
              <span className="flex items-center gap-1.5"><Mail className="size-3"/>{employee.email || "—"}</span>
              <span>Rate: <strong className="font-mono">{fmtMoney(employee.pay_rate || 0)}/hr</strong></span>
              <span>Availability: <strong>{employee.availability || "—"}</strong></span>
            </div>
          </div>
        </div>
      </Card>

      {/* Employee Summary */}
      <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Employee Summary</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="employee-summary">
        <Stat icon={Clock}      label="Current Month Hours"   value={cur.hours.toFixed(1)} />
        <Stat icon={Briefcase}  label="Current Month Jobs"    value={cur.jobs} />
        <Stat icon={DollarSign} label="Current Month Payroll" value={fmtMoney(cur.payroll)} />
        <Stat icon={Clock}      label="Year-to-Date Hours"    value={ytd.hours.toFixed(1)} />
        <Stat icon={Briefcase}  label="Year-to-Date Jobs"     value={ytd.jobs} />
        <Stat icon={DollarSign} label="Year-to-Date Payroll"  value={fmtMoney(ytd.payroll)} />
        <Stat icon={Wallet}     label="All-Time Payroll"      value={fmtMoney(all_time?.payroll || 0)} tone="text-primary" />
        <Stat icon={Award}      label="Average Pay Per Job"   value={fmtMoney(all_time?.avg_per_job || 0)} />
      </div>

      {/* Job Pay Items — explicit ledger */}
      <Card className="p-6 rounded-xl mb-6" data-testid="job-pay-items-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold">Job Pay Items</div>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-pay-item-btn">
            <Plus className="size-4 mr-1.5"/>Add Job Pay
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground italic" data-testid="pay-items-empty">
            No job pay items yet. Click <strong>+ Add Job Pay</strong> to record an entry.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="pay-items-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground text-left">
                  <th className="p-3 font-semibold">Date</th>
                  <th className="p-3 font-semibold">Client</th>
                  <th className="p-3 font-semibold">Service</th>
                  <th className="p-3 font-semibold">Address</th>
                  <th className="p-3 font-semibold">Pay Type</th>
                  <th className="p-3 font-semibold text-right">Hours</th>
                  <th className="p-3 font-semibold text-right">Rate / Fixed</th>
                  <th className="p-3 font-semibold text-right">Total Pay</th>
                  <th className="p-3 font-semibold">Status</th>
                  <th className="p-3 font-semibold">Notes</th>
                  <th className="p-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const hourly = (it.pay_type || "hourly") === "hourly";
                  return (
                    <tr key={it.id} className="border-t border-border hover:bg-muted/30" data-testid={`pay-item-row-${it.id}`}>
                      <td className="p-3 whitespace-nowrap">{fmtDate(it.date)}</td>
                      <td className="p-3">{it.client_name || "—"}</td>
                      <td className="p-3">{it.service || "—"}</td>
                      <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate" title={it.address || ""}>{it.address || "—"}</td>
                      <td className="p-3 text-xs">{hourly ? "Hourly" : "Fixed"}</td>
                      <td className="p-3 text-right font-mono">{hourly ? Number(it.hours || 0).toFixed(1) : "—"}</td>
                      <td className="p-3 text-right font-mono">{hourly ? `${fmtMoney(it.rate || 0)}/hr` : fmtMoney(it.fixed_amount || 0)}</td>
                      <td className="p-3 text-right font-mono font-semibold">{fmtMoney(it.total_pay || 0)}</td>
                      <td className="p-3">
                        <button
                          onClick={() => togglePaid(it)}
                          data-testid={`toggle-status-${it.id}`}
                          className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold transition ${it.payment_status === "Paid"
                            ? "bg-secondary/15 text-secondary border-secondary/30 hover:bg-secondary/25"
                            : "bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/20"}`}
                          title="Click to toggle paid status"
                        >
                          {it.payment_status || "Unpaid"}
                        </button>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-[180px] truncate" title={it.notes || ""}>{it.notes || "—"}</td>
                      <td className="p-3">
                        <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={() => removeItem(it)} data-testid={`delete-pay-item-${it.id}`} title="Delete pay item">
                          <Trash2 className="size-3.5"/>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/30 border-t-2 border-border">
                <tr className="font-semibold" data-testid="pay-items-totals">
                  <td className="p-3 text-xs uppercase tracking-wider text-muted-foreground" colSpan="5">Totals</td>
                  <td className="p-3 text-right font-mono">{totals.hours.toFixed(1)}</td>
                  <td className="p-3"></td>
                  <td className="p-3 text-right font-mono text-primary" data-testid="totals-earned">{fmtMoney(totals.earned)}</td>
                  <td className="p-3" colSpan="3">
                    <div className="flex gap-3 text-xs">
                      <span className="text-secondary" data-testid="totals-paid">Paid: <strong className="font-mono">{fmtMoney(totals.paid)}</strong></span>
                      <span className="text-destructive" data-testid="totals-unpaid">Unpaid: <strong className="font-mono">{fmtMoney(totals.unpaid)}</strong></span>
                      <span className="text-muted-foreground" data-testid="totals-jobs">{totals.jobs} {totals.jobs === 1 ? "item" : "items"}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Jobs & Pay History (auto-derived, read-only) */}
      <Card className="p-6 rounded-xl mb-6" data-testid="employee-jobs-card">
        <div className="font-display text-lg font-semibold mb-1">Jobs &amp; Pay History</div>
        <div className="text-xs text-muted-foreground mb-4">Auto-derived from completed jobs where this employee was assigned a pay rate.</div>
        {(jobs || []).filter((j) => j.status === "Completed").length === 0 ? (
          <div className="text-sm text-muted-foreground italic" data-testid="employee-jobs-empty">
            No completed jobs assigned yet.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="employee-jobs">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground text-left">
                  <th className="p-3 font-semibold">Date</th>
                  <th className="p-3 font-semibold">Client Name</th>
                  <th className="p-3 font-semibold">Job / Service</th>
                  <th className="p-3 font-semibold">Property Address</th>
                  <th className="p-3 font-semibold">Payment Type</th>
                  <th className="p-3 font-semibold text-right">Hours Worked</th>
                  <th className="p-3 font-semibold text-right">Pay Amount</th>
                </tr>
              </thead>
              <tbody>
                {(jobs || []).filter((j) => j.status === "Completed").map((j) => (
                  <tr key={j.id} className="border-t border-border hover:bg-muted/30" data-testid={`employee-job-row-${j.id}`}>
                    <td className="p-3 whitespace-nowrap">{fmtDate(j.date)}</td>
                    <td className="p-3">{j.client_name || "—"}</td>
                    <td className="p-3">{j.service || "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground max-w-[220px] truncate" title={j.address || ""}>{j.address || "—"}</td>
                    <td className="p-3 text-xs">{j.emp_pay_type === "Fixed" ? "Fixed" : `Hourly @ ${fmtMoney(j.emp_hourly_rate || 0)}/hr`}</td>
                    <td className="p-3 text-right font-mono">{j.emp_pay_type === "Fixed" ? "—" : (j.emp_hours || 0).toFixed(1)}</td>
                    <td className="p-3 text-right font-mono font-semibold">{fmtMoney(j.emp_pay || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AddPayItemDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        employee={employee}
        jobs={jobs || []}
        existingItems={items}
        onSaved={() => { setAddOpen(false); load(); }}
      />
    </Layout>
  );
}

function computeTotals(items) {
  let hours = 0, earned = 0, paid = 0, unpaid = 0;
  for (const it of items) {
    const hr = (it.pay_type || "hourly") === "hourly" ? Number(it.hours || 0) : 0;
    const total = Number(it.total_pay || 0);
    hours += hr;
    earned += total;
    if (it.payment_status === "Paid") paid += total;
    else unpaid += total;
  }
  return { jobs: items.length, hours, earned, paid, unpaid };
}

function Stat({ icon: Icon, label, value, tone = "" }) {
  return (
    <div className="stat-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <Icon className="size-4 text-primary"/>
      </div>
      <div className={`font-display text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

function AddPayItemDialog({ open, onClose, employee, jobs, existingItems, onSaved }) {
  const empRate = Number(employee?.pay_rate || 0);
  const completedJobs = useMemo(
    () => (jobs || []).filter((j) => j.status === "Completed"),
    [jobs]
  );
  const usedJobIds = useMemo(
    () => new Set((existingItems || []).map((i) => i.job_id).filter(Boolean)),
    [existingItems]
  );

  const blank = {
    mode: "linked", // 'linked' | 'manual'
    job_id: "",
    date: todayISO(),
    client_name: "",
    service: "",
    address: "",
    pay_type: "hourly",
    hours: 0,
    rate: empRate,
    fixed_amount: 0,
    payment_status: "Unpaid",
    notes: "",
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (open) setForm({ ...blank, rate: empRate });
  }, [open, empRate]);

  const onPickJob = (job_id) => {
    const job = completedJobs.find((j) => j.id === job_id);
    if (!job) { setForm({ ...form, job_id }); return; }
    const assignedHours = Number(job.emp_hours || 0);
    setForm({
      ...form,
      job_id,
      date: job.date || todayISO(),
      client_name: job.client_name || "",
      service: job.service || "",
      address: job.address || "",
      pay_type: job.emp_pay_type === "Fixed" ? "fixed" : "hourly",
      hours: assignedHours,
      rate: Number(job.emp_hourly_rate || empRate),
      fixed_amount: Number(job.emp_pay || 0),
    });
  };

  const totalPay = useMemo(() => {
    if (form.pay_type === "fixed") return Number(form.fixed_amount || 0);
    return Number(form.hours || 0) * Number(form.rate || 0);
  }, [form.pay_type, form.hours, form.rate, form.fixed_amount]);

  const save = async () => {
    if (form.mode === "linked" && !form.job_id) {
      toast.error("Pick a completed job or switch to Manual entry");
      return;
    }
    if (!form.client_name || !form.service) {
      toast.error("Client name and service are required");
      return;
    }
    if (form.pay_type === "hourly" && (Number(form.hours) <= 0 || Number(form.rate) <= 0)) {
      toast.error("Hours and rate must be greater than zero for hourly pay");
      return;
    }
    if (form.pay_type === "fixed" && Number(form.fixed_amount) <= 0) {
      toast.error("Fixed pay amount must be greater than zero");
      return;
    }
    const payload = {
      employee_id: employee.id,
      job_id: form.mode === "linked" ? form.job_id : null,
      date: form.date,
      client_name: form.client_name,
      service: form.service,
      address: form.address,
      pay_type: form.pay_type,
      hours: form.pay_type === "hourly" ? Number(form.hours || 0) : 0,
      rate: form.pay_type === "hourly" ? Number(form.rate || 0) : 0,
      fixed_amount: form.pay_type === "fixed" ? Number(form.fixed_amount || 0) : 0,
      total_pay: Number(totalPay.toFixed(2)),
      payment_status: form.payment_status,
      notes: form.notes,
    };
    await endpoints.create("job_pay_items", payload);
    toast.success("Job pay item added");
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="add-pay-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add Job Pay</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-2">
          <Button
            size="sm"
            variant={form.mode === "linked" ? "default" : "outline"}
            onClick={() => setForm({ ...form, mode: "linked" })}
            data-testid="mode-linked"
          >Link to Completed Job</Button>
          <Button
            size="sm"
            variant={form.mode === "manual" ? "default" : "outline"}
            onClick={() => setForm({ ...form, mode: "manual", job_id: "" })}
            data-testid="mode-manual"
          >Manual Entry</Button>
        </div>

        {form.mode === "linked" && (
          <div className="space-y-1.5">
            <Label>Completed Job</Label>
            <Select value={form.job_id || ""} onValueChange={onPickJob}>
              <SelectTrigger data-testid="pick-job-trigger"><SelectValue placeholder="Select a completed job"/></SelectTrigger>
              <SelectContent>
                {completedJobs.length === 0 && <SelectItem value="__none" disabled>No completed jobs for this employee</SelectItem>}
                {completedJobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {fmtDate(j.date)} · {j.service} · {j.client_name}{usedJobIds.has(j.id) ? " (already added)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pi-date"/>
          </div>
          <div className="space-y-1.5">
            <Label>Client Name</Label>
            <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="pi-client"/>
          </div>
          <div className="space-y-1.5">
            <Label>Job / Service</Label>
            <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="pi-service"/>
          </div>
          <div className="space-y-1.5">
            <Label>Property Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="pi-address"/>
          </div>
          <div className="space-y-1.5">
            <Label>Pay Type</Label>
            <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
              <SelectTrigger data-testid="pi-pay-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="fixed">Fixed Job Pay</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Payment Status</Label>
            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
              <SelectTrigger data-testid="pi-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="Unpaid">Unpaid</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.pay_type === "hourly" ? (
            <>
              <div className="space-y-1.5">
                <Label>Hours Worked</Label>
                <Input type="number" step="0.25" min="0" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="pi-hours"/>
              </div>
              <div className="space-y-1.5">
                <Label>Hourly Rate ($)</Label>
                <Input type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="pi-rate"/>
              </div>
            </>
          ) : (
            <div className="space-y-1.5 col-span-2">
              <Label>Fixed Pay Amount ($)</Label>
              <Input type="number" step="0.01" min="0" value={form.fixed_amount} onChange={(e) => setForm({ ...form, fixed_amount: e.target.value })} data-testid="pi-fixed"/>
            </div>
          )}

          <div className="space-y-1.5 col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pi-notes"/>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 p-3 bg-muted/30 rounded-lg">
          <span className="text-sm text-muted-foreground">Total Pay</span>
          <span className="font-display text-2xl font-bold text-primary" data-testid="pi-total">{fmtMoney(totalPay)}</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="pi-save">Save Job Pay</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
